import React, { useState, useEffect } from "react";
import { Plus, Search, Filter, Eye, Building2, X, Clock, User, Phone as PhoneIcon, FileText, Calendar, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import { StatusBadge } from "../components/common/StatusBadge";
import { QuickActions } from "../components/common/QuickActions";
import { useAuth } from "../context/AuthContext";
import { apiRequest, formatPhone, formatDateTime, formatDate, priorityOptions } from "../lib/utils";
import { toast } from "sonner";

export const ComplaintsPage = () => {
  const { user, isAdmin } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [cres, setCres] = useState([]);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewComplaint, setViewComplaint] = useState(null);  // For viewing complaint details
  
  // New complaint form
  const [newComplaint, setNewComplaint] = useState({
    customer_id: "",
    branch_id: "",
    subject: "",
    description: "",
    priority: "medium"
  });

  useEffect(() => {
    fetchComplaints();
    fetchCustomers();
    if (isAdmin()) {
      fetchBranches();
      fetchCREs();
    }
  }, [statusFilter]);

  const fetchComplaints = async () => {
    try {
      let url = "/complaints";
      const params = [];
      if (statusFilter && statusFilter !== "all") {
        params.push(`status=${statusFilter}`);
      }
      // For CRE users, use cre_user_id to filter by their assigned branches
      if (!isAdmin() && user?.id) {
        params.push(`cre_user_id=${user.id}`);
      }
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }
      const data = await apiRequest(url);
      setComplaints(data);
    } catch (error) {
      toast.error("Failed to fetch complaints");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const data = await apiRequest("/customers");
      setCustomers(data);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const fetchBranches = async () => {
    try {
      const data = await apiRequest("/branches");
      setBranches(data);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const fetchCREs = async () => {
    try {
      const data = await apiRequest("/users?role=cre");
      setCres(data);
    } catch (error) {
      console.error("Failed to fetch CREs:", error);
    }
  };

  const handleAddComplaint = async (e) => {
    e.preventDefault();
    try {
      await apiRequest("/complaints", {
        method: "POST",
        body: JSON.stringify(newComplaint)
      });
      toast.success("Complaint registered successfully");
      setShowAddModal(false);
      setNewComplaint({
        customer_id: "",
        branch_id: "",
        subject: "",
        description: "",
        priority: "medium"
      });
      fetchComplaints();
    } catch (error) {
      toast.error(error.message || "Failed to register complaint");
    }
  };

  const handleUpdateStatus = async (complaintId, newStatus) => {
    try {
      await apiRequest(`/complaints/${complaintId}`, {
        method: "PUT",
        body: JSON.stringify({ status: newStatus })
      });
      toast.success("Complaint status updated");
      fetchComplaints();
    } catch (error) {
      toast.error("Failed to update complaint status");
    }
  };

  const filteredComplaints = complaints.filter((complaint) => {
    const matchesSearch =
      complaint.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      complaint.contact_phone?.includes(searchTerm);
    return matchesSearch;
  });

  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "open", label: "Open" },
    { value: "in_progress", label: "In Progress" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "text-red-600 bg-red-50";
      case "medium":
        return "text-yellow-600 bg-yellow-50";
      case "low":
        return "text-green-600 bg-green-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  return (
    <div className="space-y-6" data-testid="complaints-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Complaints</h1>
          <p className="text-muted-foreground">Track and resolve customer complaints</p>
        </div>
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button data-testid="add-complaint-button">
              <Plus className="w-4 h-4 mr-2" />
              New Complaint
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-heading">Register Complaint</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddComplaint} className="space-y-4">
              <div>
                <Label htmlFor="customer">Customer</Label>
                <Select
                  value={newComplaint.customer_id}
                  onValueChange={(value) => setNewComplaint({ ...newComplaint, customer_id: value })}
                >
                  <SelectTrigger data-testid="complaint-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name} - {formatPhone(customer.phone)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isAdmin() && (
                <div>
                  <Label htmlFor="branch">Branch</Label>
                  <Select
                    value={newComplaint.branch_id}
                    onValueChange={(value) => setNewComplaint({ ...newComplaint, branch_id: value })}
                  >
                    <SelectTrigger data-testid="complaint-branch-select">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  value={newComplaint.subject}
                  onChange={(e) => setNewComplaint({ ...newComplaint, subject: e.target.value })}
                  placeholder="Brief description of the issue"
                  required
                  data-testid="complaint-subject-input"
                />
              </div>
              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={newComplaint.priority}
                  onValueChange={(value) => setNewComplaint({ ...newComplaint, priority: value })}
                >
                  <SelectTrigger data-testid="complaint-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorityOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={newComplaint.description}
                  onChange={(e) => setNewComplaint({ ...newComplaint, description: e.target.value })}
                  rows={4}
                  placeholder="Detailed description of the complaint..."
                  required
                  data-testid="complaint-description-input"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-complaint-button">
                  Register Complaint
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="bento-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by subject, name, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="complaint-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="complaint-status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Complaints Table */}
      <Card className="bento-card">
        <CardContent className="p-0">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-high-density">
                  <TableHead>Subject</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredComplaints.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No complaints found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredComplaints.map((complaint) => (
                    <TableRow key={complaint.id} className="table-high-density" data-testid={`complaint-row-${complaint.id}`}>
                      <TableCell className="font-medium max-w-[200px] truncate">
                        {complaint.subject}
                      </TableCell>
                      <TableCell>{complaint.contact_name || "-"}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatPhone(complaint.contact_phone)}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(complaint.priority)}`}>
                          {complaint.priority?.toUpperCase()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={complaint.status}
                          onValueChange={(value) => handleUpdateStatus(complaint.id, value)}
                        >
                          <SelectTrigger className="w-[130px] h-8">
                            <StatusBadge status={complaint.status} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDateTime(complaint.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            onClick={() => setViewComplaint(complaint)}
                            data-testid={`view-complaint-${complaint.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {complaint.contact_phone && (
                            <QuickActions
                              phone={complaint.contact_phone}
                              showHistory={false}
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Complaint View Modal */}
      <Sheet open={!!viewComplaint} onOpenChange={(open) => !open && setViewComplaint(null)}>
        <SheetContent className="w-full sm:max-w-[500px] overflow-y-auto" data-testid="complaint-view-modal">
          <SheetHeader className="space-y-1">
            <SheetTitle className="font-heading text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#ED1C24]" />
              Complaint Details
            </SheetTitle>
          </SheetHeader>
          
          {viewComplaint && (
            <div className="mt-6 space-y-6">
              {/* Subject & Priority */}
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-semibold text-lg">{viewComplaint.subject}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getPriorityColor(viewComplaint.priority)}`}>
                    {viewComplaint.priority?.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={viewComplaint.status} />
                  {viewComplaint.resolved_at && (
                    <span className="text-xs text-muted-foreground">
                      Resolved {formatDateTime(viewComplaint.resolved_at)}
                    </span>
                  )}
                </div>
              </div>

              <Separator />

              {/* Customer Info */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Customer Information
                </h4>
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Name</span>
                    <span className="font-medium">{viewComplaint.contact_name || "-"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Phone</span>
                    <span className="font-mono text-sm">{formatPhone(viewComplaint.contact_phone) || "-"}</span>
                  </div>
                  {viewComplaint.branch_id && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Branch</span>
                      <span className="text-sm">{branches.find(b => b.id === viewComplaint.branch_id)?.name || viewComplaint.branch_id}</span>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Description */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Complaint Description
                </h4>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm whitespace-pre-wrap">
                    {viewComplaint.description || "No description provided."}
                  </p>
                </div>
              </div>

              <Separator />

              {/* Timeline */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Timeline
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Created</span>
                    <span>{formatDateTime(viewComplaint.created_at)}</span>
                  </div>
                  {viewComplaint.updated_at && viewComplaint.updated_at !== viewComplaint.created_at && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Last Updated</span>
                      <span>{formatDateTime(viewComplaint.updated_at)}</span>
                    </div>
                  )}
                  {viewComplaint.resolved_at && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Resolved</span>
                      <span className="text-green-600">{formatDateTime(viewComplaint.resolved_at)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                {viewComplaint.contact_phone && (
                  <>
                    <a
                      href={`tel:${viewComplaint.contact_phone}`}
                      className="flex-1"
                    >
                      <Button variant="outline" className="w-full" data-testid="complaint-call-btn">
                        <PhoneIcon className="w-4 h-4 mr-2" />
                        Call
                      </Button>
                    </a>
                    <a
                      href={`https://wa.me/91${viewComplaint.contact_phone?.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button variant="outline" className="w-full text-green-600 border-green-200 hover:bg-green-50" data-testid="complaint-whatsapp-btn">
                        WhatsApp
                      </Button>
                    </a>
                  </>
                )}
              </div>

              {/* Status Update */}
              <div className="space-y-3 pt-2">
                <Label>Update Status</Label>
                <Select
                  value={viewComplaint.status}
                  onValueChange={(value) => {
                    handleUpdateStatus(viewComplaint.id, value);
                    setViewComplaint({ ...viewComplaint, status: value });
                  }}
                >
                  <SelectTrigger data-testid="complaint-status-update">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ComplaintsPage;
